import { useEffect, useState } from 'react';
import User from '../../membership/models/User';
import MembershipService from '../../membership/services/MembershipService';
import { useAssetStorage } from '../../shared/hooks/useAssetStorage';
import AuthService from '../../shared/services/AuthService';
import ProfileService from '../../shared/services/ProfileService';
import { IUserAvatarContext } from './AvatarContext';

/**
 * Hook to handle loading and switching of current user's avatar, as used in the
 * <AvatarContextProvider> component.
 */
const useUserAvatar = (user: User | undefined) => {
  const { upload: assetStorageUpload } = useAssetStorage(AuthService.Tenant);
  /** User's friendly name to show as avatar image alt text. */
  const [friendlyname, setFriendlyname] = useState<string | undefined>();
  /** User's initials displayed if no avatar image specified. */
  const [initials, setInitials] = useState<string | undefined>();
  /** Image URL including sas token. */
  const [src, setSrc] = useState<string | undefined>();
  /** Value indicating whether a new upload is in progress. */
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.userkey) {
      MembershipService.getAvatarUrl(user.userkey).then(setSrc);
    }
  }, [user?.userkey]);

  useEffect(() => {
    if (user) {
      setFriendlyname(
        user.friendlyname || `${user.firstname} ${user.friendlyname}`
      );
      setInitials(ProfileService.getInitials(user.firstname, user.lastname));
    }
  }, [user?.firstname, user?.lastname, user?.friendlyname]);

  /** Upload the image and set it as the new avatar. */
  const upload = async (file: File): Promise<string> => {
    setUploading(true);
    return new Promise(async (resolve) => {
      try {
        const asset = (await assetStorageUpload([file], 'learner_assets'))[0];
        const subscription = asset.uploadObservable.subscribe({
          error: (_) => {
            setUploading(false);
            subscription.unsubscribe();
          },
          complete: () => {
            subscription.unsubscribe();
            const { assetpath } = asset.metadata;
            MembershipService.setAvatarUrl(assetpath)
              .then(() => MembershipService.getAvatarUrl(AuthService.Userkey))
              .then((value: string) => {
                setSrc(value);
                setUploading(false);
                resolve(assetpath);
              });
          }
        });
      } catch (e) {
        // NOP
      } finally {
        setUploading(false);
      }
    });
  };
  /** Remove the user's avatar. */
  const remove = async () => {
    setSrc(undefined);
    await MembershipService.removeAvatar();
  };

  const avatarContextProvider: IUserAvatarContext = {
    friendlyname,
    initials,
    src,
    uploading,
    setSrc,
    upload,
    remove
  };

  return avatarContextProvider;
};

export default useUserAvatar;
